import React from 'react';
import CustomParagraph from './CustomParagraph';
import CustomButton from './CustomButton';
import { Button, Flex, Typography } from 'antd';
import UseAi from '../assets/images/UseAi';
import ExpandMore from '../assets/images/ExpandMore';

interface AlertCardPreviewProps {
  showButtons?: boolean;
  cardStyle: 'style-1' | 'style-2';
  icon: React.ReactElement;
  createdAt: string;
  title: string;
  description: string;
  recommendedAction?: string;
  isActionFocused?: boolean;
  bgColor: string;
  color: string;
  actionButtonText: string;
  actionButtonBorderRadius: string;
  actionButtonBgColor: string;
  actionButtonOnClick?: () => void;
  dismissAction?: () => void;
  IS_TP_EXCEPTION?: boolean;
}

/**
 * Alert card component displaying notification information with customizable layout and actions
 *
 * @param showButtons - Whether to show action buttons (default: true)
 * @param cardStyle - Layout style: 'style-1' (horizontal) or 'style-2' (vertical with multiple buttons)
 * @param icon - React element for the alert icon
 * @param createdAt - Timestamp when the alert was created
 * @param title - Alert title text
 * @param description - Alert description text
 * @param recommendedAction - Optional recommended action text
 * @param isActionFocused - Whether to highlight the recommended action
 * @param bgColor - Background color for the card
 * @param color - Text color
 * @param actionButtonText - Text for the main action button
 * @param actionButtonBorderRadius - Border radius for action button
 * @param actionButtonBgColor - Background color for action button
 * @param actionButtonOnClick - Click handler for action button
 * @param dismissAction - Optional click handler for dismiss button
 */
const AlertCardPreview: React.FC<AlertCardPreviewProps> = ({
  showButtons = true,
  cardStyle,
  icon,
  createdAt,
  title,
  description,
  recommendedAction,
  isActionFocused,
  bgColor,
  color,
  actionButtonText,
  actionButtonBorderRadius,
  actionButtonBgColor,
  actionButtonOnClick,
  dismissAction,
  IS_TP_EXCEPTION,
}) => {
  const CardContent = (
    <Flex
      align='center'
      justify='space-between'
      gap={cardStyle === 'style-2' ? 8 : 16}
      vertical={cardStyle === 'style-2'}
      style={{
        padding: '16px',
        background: bgColor,
        borderRadius: '12px',
      }}
    >
      <Flex gap={16} align='center' style={{ width: '100%' }}>
        {React.cloneElement(icon, { style: { fontSize: 16, color } })}
        <div className='w-full'>
          <Flex gap={8} align='center' justify='space-between'>
            {/* The card fill is a fixed accent (yellow/red) in both themes, so the
                text colour must stay literal: CustomParagraph's `color` prop would
                remap black to the theme text token and turn it white in dark mode. */}
            <CustomParagraph
              text={title}
              fontSize='12px'
              fontWeight={600}
              lineHeight='20px'
              style={{ color }}
              marginBottom='4px'
            />
            <CustomParagraph
              text={'2 hours ago'}
              fontSize='12px'
              fontWeight={600}
              lineHeight='17px'
              marginBottom='4px'
              style={{ color, whiteSpace: 'nowrap', opacity: 0.6 }}
            />
          </Flex>
          {IS_TP_EXCEPTION && (
            <div className='alert-preview-message'>
              Message: <span> (Provider message)</span>
            </div>
          )}
          <Typography.Paragraph
            style={{
              fontSize: 12,
              fontWeight: 400,
              lineHeight: '20px',
              color: color,
              marginBottom: 0,
            }}
          >
            {description + ' '}
            {recommendedAction && (
              // actionChange
              <span
                className={isActionFocused ? 'highlight-alert-text' : undefined}
              >
                {recommendedAction}
              </span>
            )}
          </Typography.Paragraph>

          <CustomParagraph
            text={createdAt}
            fontSize='12px'
            fontWeight={400}
            lineHeight='20px'
            style={{ color }}
            marginBottom='0'
          />
        </div>
      </Flex>

      <>
        {showButtons === true && cardStyle === 'style-1' && (
          <CustomButton
            text={actionButtonText}
            width='auto'
            padding='5px 15px'
            height='36px'
            color={color}
            bgColor={actionButtonBgColor}
            borderRadius={actionButtonBorderRadius}
            marginBottom='0'
            boxShadow='none'
            onClick={actionButtonOnClick}
          />
        )}

        {cardStyle === 'style-2' && (
          <Flex gap={8} justify='flex-end' wrap style={{ width: '100%' }}>
            <CustomButton
              text='Dismiss'
              width='auto'
              padding='5px 15px'
              fontSize={'12px'}
              fontWeight={'600'}
              height='36px'
              color={color}
              bgColor={'initial'}
              boxShadow={'none'}
              onClick={dismissAction}
            />

            <CustomButton
              text={actionButtonText}
              width='auto'
              fontSize={'12px'}
              fontWeight={'600'}
              padding='5px 15px'
              height='36px'
              color={color}
              bgColor={actionButtonBgColor}
              borderRadius={actionButtonBorderRadius}
              marginBottom='0'
              boxShadow='none'
              onClick={actionButtonOnClick}
            />

            <Button
              style={{
                height: '36px',
                backgroundColor: actionButtonBgColor,
                borderRadius: '8px',
                fontSize: '12px',
                fontWeight: 600,
                padding: '0 12px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                border: 'none',
                boxShadow: 'none',
                outline: 'none',
              }}
            >
              <UseAi color={color} />
              <Typography.Text
                style={{ fontSize: '12px', fontWeight: 600, color: color }}
              >
                Use AI
              </Typography.Text>
              <ExpandMore color={color} />
            </Button>
          </Flex>
        )}
      </>
    </Flex>
  );

  return CardContent;
};

export default AlertCardPreview;
